// src/components/layout/AppLayout.jsx — Authenticated app shell
import { useState, useEffect } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { notificationsApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LOGOUT } from "@/constants/testIds/auth";
import sidebarBg from "@/assets/images/sidebar.jpeg";
import logoImg from "@/assets/images/logo.jpg";
import {
  LayoutDashboard,
  Plane,
  Receipt,
  Wallet,
  Building2,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  ChevronLeft,
  Menu,
  User,
  Search,
  ChevronDown,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

const navItems = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    href: "/app/dashboard",
    roles: ["employee", "manager", "travel_desk", "finance", "admin"],
  },
  {
    label: "Travel Requests",
    icon: Plane,
    href: "/app/travel-requests",
    roles: ["employee", "manager", "travel_desk", "admin"],
  },
  {
    label: "Expense Claims",
    icon: Receipt,
    href: "/app/expense-claims",
    roles: ["employee", "manager", "finance", "admin"],
  },
  {
    label: "Cash Advances",
    icon: Wallet,
    href: "/app/cash-advances",
    roles: ["employee", "manager", "finance", "admin"],
  },
  {
    label: "Vendors",
    icon: Building2,
    href: "/app/vendors",
    roles: ["travel_desk", "admin"],
  },
  {
    label: "Reports",
    icon: BarChart3,
    href: "/app/reports",
    roles: ["manager", "finance", "admin"],
  },
  {
    label: "Policy Config",
    icon: Settings,
    href: "/app/policies",
    roles: ["admin"],
  },
  { label: "Team", icon: User, href: "/app/team", roles: ["manager", "admin"] },
];

const roleBadgeColor = {
  employee: "bg-blue-100 text-blue-800 border-blue-200",
  manager: "bg-purple-100 text-purple-800 border-purple-200",
  travel_desk: "bg-orange-100 text-orange-800 border-orange-200",
  finance: "bg-green-100 text-green-800 border-green-200",
  admin: "bg-red-100 text-red-800 border-red-200",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotificationCount = async () => {
    try {
      const { data } = await notificationsApi.count();
      setUnreadCount(parseInt(data?.count || 0, 10));
    } catch (e) {}
  };

  useEffect(() => {
    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 8000);
    return () => clearInterval(interval);
  }, [pathname]);

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const handleConfirmSignOut = async () => {
    setShowSignOutModal(false);
    await logout();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/app/travel-requests?search=${encodeURIComponent(searchQuery)}`);
  };

  const handleMouseEnter = () => {
    if (collapsed) {
      setCollapsed(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setCollapsed(true);
  };

  const visibleNav = navItems.filter((item) => item.roles.includes(user?.role));

  const SidebarContent = () => (
    <div className="flex flex-col h-full relative overflow-hidden bg-slate-900">
      {/* Background image on bottom side of sidebar */}
      <div
        className="absolute bottom-0 inset-x-0 h-80 bg-cover bg-bottom bg-no-repeat pointer-events-none opacity-95 z-0"
        style={{ backgroundImage: `url(${sidebarBg})` }}
      >
        {/* Soft top gradient to blend cleanly into dark sidebar background */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900/30 to-transparent" />
      </div>

      <div className="relative z-10 flex flex-col h-full">
        {/* Logo Header */}
        <div
          className={cn(
            "flex items-center justify-between p-4 border-b border-slate-800/80 bg-slate-900/90 backdrop-blur-md",
            collapsed && "justify-center",
          )}
        >
          <div className="flex items-center gap-3">
            <img
              src={logoImg}
              alt="CTEMS Logo"
              className="w-9 h-9 rounded-lg object-contain shrink-0"
            />
            {!collapsed && (
              <div className="flex flex-col">
                <span className="font-extrabold text-white text-base tracking-tight leading-tight">
                  CTEMS{" "}
                  <span className="text-blue-400 text-xs font-semibold uppercase ml-0.5">
                    Travel
                  </span>
                </span>
                <span className="text-[10px] text-slate-400 font-medium tracking-wider uppercase">
                  Corporate Portal
                </span>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              onClick={handleClose}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Close sidebar"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Nav Menu */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const active = pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                  active
                    ? "bg-blue-600 text-white font-semibold shadow-md"
                    : "text-slate-200 hover:text-white hover:bg-slate-800/80 backdrop-blur-xs",
                  collapsed && "justify-center px-2",
                )}
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section with sign-out */}
        <div className="p-3 border-t border-slate-800/60 bg-slate-950/60 backdrop-blur-md">
          {!collapsed ? (
            <button
              onClick={handleSignOutClick}
              data-testid={LOGOUT.button}
              className="flex items-center px-3 py-2.5 gap-2.5 text-white text-xs w-full rounded-lg hover:bg-white/20 transition-all cursor-pointer font-medium drop-shadow-sm"
            >
              <LogOut className="w-4 h-4 text-slate-200" />
              <span>Sign out</span>
            </button>
          ) : (
            <button
              onClick={handleSignOutClick}
              data-testid={LOGOUT.button}
              className="flex items-center justify-center p-2.5 text-white text-xs w-full rounded-lg hover:bg-white/20 transition-all cursor-pointer drop-shadow-sm"
              title="Sign out"
            >
              <LogOut className="w-4 h-4 text-slate-200" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Desktop Sidebar */}
      <aside
        onMouseEnter={handleMouseEnter}
        className={cn(
          "hidden md:flex flex-col bg-slate-900 transition-all duration-300 ease-in-out shrink-0 z-30 shadow-xl",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative z-50 w-60 h-full bg-slate-900">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-slate-200 px-3 py-2 sm:px-4 sm:py-2.5 flex items-center justify-between shrink-0 shadow-sm gap-3 sm:gap-4">
          <div className="flex items-center gap-3 flex-1 max-w-xl">
            <button
              className="md:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Quick Search */}
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-full max-w-xs hidden sm:block"
            >
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search requests or claims..."
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </form>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/app/notifications"
              className="relative p-2 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              title="Notifications & Activity Alerts"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-extrabold text-white shadow-xs ring-2 ring-white animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>

            <div className="h-5 w-px bg-slate-200" />

            {/* User details dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors focus:outline-none cursor-pointer">
                  <Avatar className="w-7 h-7 border border-slate-200">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                      {user?.name ? user.name.slice(0, 2).toUpperCase() : "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-700 font-semibold truncate max-w-[120px]">
                      {user?.name?.split(" ")[0]}
                    </span>
                    <span
                      className={cn(
                        "text-[10px] px-2 py-0.5 rounded-md font-semibold capitalize border hidden sm:inline-block",
                        roleBadgeColor[user?.role],
                      )}
                    >
                      {user?.role?.replace("_", " ")}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-semibold leading-none text-slate-900">{user?.name}</p>
                    <p className="text-xs leading-none text-slate-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOutClick}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer font-medium"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content View */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 bg-slate-50">
          <Outlet />
        </main>
      </div>

      {/* Sign Out Confirmation Modal */}
      <AlertDialog open={showSignOutModal} onOpenChange={setShowSignOutModal}>
        <AlertDialogContent className="sm:max-w-md" data-testid={LOGOUT.modal}>
          <AlertDialogHeader>
            <div className="mx-auto sm:mx-0 w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-1">
              <LogOut className="w-5 h-5" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Confirm Sign Out
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Are you sure you want to sign out of your account? You will need to log back in to access your dashboard and requests.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              data-testid={LOGOUT.cancelButton}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSignOut}
              data-testid={LOGOUT.confirmButton}
              className={cn(buttonVariants({ variant: "destructive" }), "bg-red-600 hover:bg-red-700 text-white font-medium")}
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

